import clsx from "clsx";
import React, { ReactNode } from "react";

import { useMediaQuery } from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Breakpoints from "common/breakpoints";
import { DASH } from "common/constants";
import CourseTypeSymbol from "components/CourseTypeSymbol";
import { DistanceUnit } from "interfaces/DistanceUnit";
import { transformViewDate } from "utils/date-utils";
import {
  convertDistanceToFurlongs,
  getLengthFract,
  getRaceDistanceFract,
  SHORT_DISTANCE_THRESHOLD_IN_FURLONGS
} from "utils/distance-utils";
import { parseTrackCondition } from "utils/enum-parser";
import ClaimedInfo from "./ClaimedInfo";

import useStyles from "./styles";

interface IProps {
  data: racehorse360.IHorseLastPastPerformance;
}

interface IPointInfo {
  lengthBehindAtPoint: number;
  lengthAheadAtPoint: number;
  position: number;
}

const ExamsTablePPsRow = (props: IProps) => {
  const { data } = props;

  const classes = useStyles();
  const matchesDownSM600 = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(Breakpoints.SM_600)
  );

  const hasClaimedInfo = Boolean(data.isClaimed?.value);

  const pointsInfo: IPointInfo[] = [
    {
      lengthAheadAtPoint: data.lengthAheadAtPointOfCall_1,
      lengthBehindAtPoint: data.lengthBehindAtPointOfCall_1,
      position: data.positionAtPointOfCall_1
    },
    {
      lengthAheadAtPoint: data.lengthAheadAtPointOfCall_2,
      lengthBehindAtPoint: data.lengthBehindAtPointOfCall_2,
      position: data.positionAtPointOfCall_2
    },
    {
      lengthAheadAtPoint: data.lengthAheadAtPointOfCall_3,
      lengthBehindAtPoint: data.lengthBehindAtPointOfCall_3,
      position: data.positionAtPointOfCall_3
    },
    {
      lengthAheadAtPoint: data.lengthAheadAtPointOfCall_4,
      lengthBehindAtPoint: data.lengthBehindAtPointOfCall_4,
      position: data.positionAtPointOfCall_4
    },
    {
      lengthAheadAtPoint: data.lengthAheadAtPointOfCall_5,
      lengthBehindAtPoint: data.lengthBehindAtPointOfCall_5,
      position: data.positionAtPointOfCall_5
    },
    {
      lengthAheadAtPoint: data.lengthAheadAtFinish,
      lengthBehindAtPoint: data.lengthBehindAtFinish,
      position: data.positionAtFinish
    }
  ];

  const comment = matchesDownSM600 ? data.shortComment : data.comment;

  const renderTemporaryRailDistance = (temporaryRailDistance: number) =>
    Boolean(temporaryRailDistance) && (
      <span className={classes.temporaryRailDistance}>
        {temporaryRailDistance}
      </span>
    );

  const renderRaceDistanceUnit = (distance: number): DistanceUnit => {
    return distance < SHORT_DISTANCE_THRESHOLD_IN_FURLONGS ? "f" : "m";
  };

  const renderRaceDistance = (
    distance: number,
    distanceUnit: number
  ): React.ReactElement => {
    const distanceInFurlongs = convertDistanceToFurlongs(
      distance,
      distanceUnit
    );

    const int =
      distanceInFurlongs < SHORT_DISTANCE_THRESHOLD_IN_FURLONGS
        ? Math.trunc(distanceInFurlongs / 100)
        : Math.trunc(distanceInFurlongs / SHORT_DISTANCE_THRESHOLD_IN_FURLONGS);
    const frac = getRaceDistanceFract(distanceInFurlongs);

    let fractionElement: ReactNode = null;
    if (frac) {
      if (frac.includes("/")) {
        const fractionParts = frac.split("/");
        fractionElement = (
          <span className={classes.fracDiagonal}>
            <span className="numerator">{fractionParts[0]}</span>
            <span className="bar">∕</span>
            <span className="denominator">{fractionParts[1]}</span>
          </span>
        );
      } else {
        fractionElement = <sup>{frac}</sup>;
      }
    }

    return (
      <>
        {Boolean(int) && <span>{int}</span>}
        {Boolean(fractionElement) && fractionElement}
        {Boolean(!fractionElement) &&
          renderRaceDistanceUnit(distanceInFurlongs)}
      </>
    );
  };

  const renderPositionShift = (length: number) => {
    if (!length) return;

    const integer = Math.trunc(length / 100);
    const fraction = getLengthFract(length);

    let fractionElement: ReactNode = null;
    if (fraction) {
      if (fraction.includes("/")) {
        const fractionParts = fraction.split("/");
        fractionElement = (
          <span className={classes.fracVertical}>
            <span className="numerator">{fractionParts[0]}</span>
            <span className="bar">/</span>
            <span className="denominator">{fractionParts[1]}</span>
          </span>
        );
      } else {
        fractionElement = <sup>{fraction}</sup>;
      }
    }

    return (
      <>
        {Boolean(integer) && <span className={classes.integer}>{integer}</span>}
        {Boolean(fractionElement) && fractionElement}
      </>
    );
  };

  const renderOdds = (odds: number = 0, isPostTimeFavorite: boolean) => {
    const formatted = (odds / 100).toLocaleString("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    });
    const trimmedOddsStr = odds >= 100 ? formatted : formatted.substring(1);
    return `${isPostTimeFavorite ? "*" : ""}${trimmedOddsStr}`;
  };

  const renderPointsInfo = () => {
    return (
      <div className={classes.pastPerformancesCell}>
        {pointsInfo.map((point, index) => {
          let resultContent: string | ReactNode = DASH;

          if (point.position) {
            const { lengthAheadAtPoint, lengthBehindAtPoint, position } = point;
            const pointValue =
              position > 1 ? lengthBehindAtPoint : lengthAheadAtPoint;

            resultContent = (
              <>
                <span>{position}&nbsp;</span>
                <span className={classes.notCompressed}>
                  {renderPositionShift(pointValue)}
                </span>
              </>
            );
          }

          return (
            <div key={index} className={classes.pointsInfo}>
              {resultContent}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      key={data.id}
      className={clsx(classes.pastPerformancesRow, { hasClaimedInfo })}
    >
      <div className={classes.ppsTableCellWrapper}>
        <div className={classes.pastPerformancesCell}>
          {transformViewDate(data.date, null, "ddMMMyy")}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div className={classes.pastPerformancesCell}>
          {`${data.country === "USA" ? data.raceNumber : "⯁"} ${data.track}`}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div className={classes.pastPerformancesCell}>
          {parseTrackCondition(data.trackCondition)}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div className={classes.pastPerformancesCell}>
          <CourseTypeSymbol
            courseType={data.courseType}
            scheduledCourseType={data.scheduledCourseType}
          />
          {renderTemporaryRailDistance(data.temporaryRailDistance)}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div className={classes.pastPerformancesCell}>
          {data.courseType ===
          racehorse360.CourseSurface.COURSE_SURFACE_DOWNHILL_TURF
            ? "✱"
            : ""}
          {renderRaceDistance(data.raceDistance, data.raceDistanceUnit)}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div
          className={clsx(
            classes.pastPerformancesCell,
            classes.ellipsis,
            classes.raceType
          )}
        >
          {data.raceType}
          {Boolean(data.raceName) && ` ${DASH} ${data.raceName}`}
          {Boolean(data.raceConditions) &&
            ` ${DASH} ${data.raceConditions?.replace(/\s+/, "")}`}
          {Boolean(data.racePurse) &&
            ` ${DASH} ${Math.round(data.racePurse / 1000)}k`}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>{renderPointsInfo()}</div>

      <div className={classes.ppsTableCellWrapper}>
        <div
          className={clsx(
            classes.pastPerformancesCell,
            classes.ellipsis,
            classes.jockeyName
          )}
        >
          {data.jockeyLastName?.replace(",", "").replace(".", "")}{" "}
          {data.jockeyFirstName?.charAt(0)}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div className={classes.pastPerformancesCell}>
          {Boolean(data.odds) &&
            renderOdds(data.odds, Boolean(data.isPostTimeFavorite?.value))}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div className={clsx(classes.pastPerformancesCell, classes.comment)}>
          {data.isTroubledTrip ? "► " : ""}
          {comment}
        </div>
      </div>

      <div className={classes.ppsTableCellWrapper}>
        <div className={classes.pastPerformancesCell}>{data.fieldSize}</div>
      </div>
      {hasClaimedInfo && <ClaimedInfo data={data} />}
    </div>
  );
};

export default React.memo(ExamsTablePPsRow);
